import { KEYBIND_ACTIONS, type KeybindAction } from "@atrium/shared";

/**
 * Key combos are stored as canonical strings: modifiers in the order Ctrl, Alt, Shift,
 * then the key, joined by "+", e.g. "N", "Ctrl+,", "Ctrl+Shift+M". "Ctrl" matches
 * Cmd on macOS.
 */

export const KEYBIND_LABELS: Record<KeybindAction, string> = {
  goCommunities: "Go to communities",
  goFeed: "Go to feed",
  goExplore: "Go to explore",
  goMessages: "Go to messages",
  goNotifications: "Go to notifications",
  goSettings: "Open settings",
  newPost: "New post",
  toggleMembers: "Show or hide members",
  toggleMute: "Mute or unmute",
  toggleDeafen: "Deafen or undeafen",
  search: "Search",
  quickSwitcher: "Quick switcher",
  shortcutHelp: "Keyboard shortcuts",
  commandPalette: "Command palette",
  markAllRead: "Mark everything as read",
};

export const KEYBIND_GROUPS: { title: string; actions: KeybindAction[] }[] = [
  { title: "Navigation", actions: ["goCommunities", "goFeed", "goExplore", "goMessages", "goNotifications", "goSettings", "search"] },
  { title: "Actions", actions: ["quickSwitcher", "commandPalette", "shortcutHelp", "newPost", "toggleMembers", "markAllRead"] },
  { title: "Voice", actions: ["toggleMute", "toggleDeafen"] },
];

export const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad|iPod/i.test(navigator.platform || navigator.userAgent);

const MODIFIER_KEYS = new Set(["Control", "Shift", "Alt", "Meta", "AltGraph", "CapsLock", "Fn", "Dead", "Unidentified", "Process", "OS"]);
const MOD_ORDER = ["Ctrl", "Alt", "Shift"] as const;
type Mod = (typeof MOD_ORDER)[number];

const MOD_ALIASES: Record<string, Mod> = { ctrl: "Ctrl", control: "Ctrl", cmd: "Ctrl", command: "Ctrl", meta: "Ctrl", mod: "Ctrl", alt: "Alt", option: "Alt", opt: "Alt", shift: "Shift" };

function normaliseKey(key: string): string {
  if (key === " " || key.toLowerCase() === "space") return "Space";
  if (key.length === 1) return key.toUpperCase();
  if (key.toLowerCase() === "esc") return "Escape";
  return key.charAt(0).toUpperCase() + key.slice(1);
}

export function parseCombo(combo: string): { mods: Set<Mod>; key: string } | null {
  const raw = combo.trim();
  if (!raw) return null;
  let key: string;
  let modParts: string[];
  if (raw === "+") {
    key = "+";
    modParts = [];
  } else if (raw.endsWith("++")) {
    key = "+";
    modParts = raw.slice(0, -2).split("+");
  } else {
    const parts = raw.split("+");
    key = parts.pop() ?? "";
    modParts = parts;
  }
  if (!key) return null;
  const mods = new Set<Mod>();
  for (const m of modParts) {
    const alias = MOD_ALIASES[m.trim().toLowerCase()];
    if (alias) mods.add(alias);
  }
  return { mods, key: normaliseKey(key) };
}

function serialise(mods: Set<Mod>, key: string) {
  return [...MOD_ORDER.filter((m) => mods.has(m)), key].join("+");
}

export function normaliseCombo(combo: string): string {
  const p = parseCombo(combo);
  return p ? serialise(p.mods, p.key) : "";
}

/** The combo for a keydown, or null for a bare modifier press. */
export function comboFromEvent(e: KeyboardEvent): string | null {
  if (MODIFIER_KEYS.has(e.key)) return null;
  // Use the physical key for letters and digits so Alt/Shift variants stay readable.
  let key = e.key;
  if (/^Key[A-Z]$/.test(e.code)) key = e.code.slice(3);
  else if (/^Digit\d$/.test(e.code)) key = e.code.slice(5);
  const mods = new Set<Mod>();
  if (e.ctrlKey || e.metaKey) mods.add("Ctrl");
  if (e.altKey) mods.add("Alt");
  if (e.shiftKey) mods.add("Shift");
  return serialise(mods, normaliseKey(key));
}

/** Single-key shortcuts are those without Ctrl/Cmd or Alt. */
export function isSingleKey(combo: string): boolean {
  const p = parseCombo(combo);
  return !!p && !p.mods.has("Ctrl") && !p.mods.has("Alt");
}

export function findAction(keybinds: Record<KeybindAction, string>, combo: string): KeybindAction | undefined {
  const target = normaliseCombo(combo);
  if (!target) return undefined;
  return KEYBIND_ACTIONS.find((a) => normaliseCombo(keybinds[a] ?? "") === target);
}

const KEY_LABELS: Record<string, string> = {
  ArrowUp: "↑",
  ArrowDown: "↓",
  ArrowLeft: "←",
  ArrowRight: "→",
  Escape: "Esc",
  Enter: "Enter",
  Backspace: "Backspace",
  Delete: "Del",
  Space: "Space",
};

/** Display parts for a combo, e.g. ["Ctrl", "Shift", "M"] or ["⌘", "⇧", "M"] on macOS. */
export function comboParts(combo: string): string[] {
  const p = parseCombo(combo);
  if (!p) return [];
  const mods = MOD_ORDER.filter((m) => p.mods.has(m)).map((m) => (isMac ? { Ctrl: "⌘", Alt: "⌥", Shift: "⇧" }[m] : m));
  return [...mods, KEY_LABELS[p.key] ?? p.key];
}

/** Short text for tooltips; empty when unset. */
export function comboLabel(combo: string): string {
  return comboParts(combo).join(isMac ? "" : "+");
}

/** Shortcuts that can't be rebound (chat, composers, lists), shown in settings and the Ctrl+/ reference. */
export function fixedShortcuts(opts: { enterToSend: boolean }): { group: string; label: string; keys: string[] }[] {
  const mod = isMac ? "⌘" : "Ctrl";
  return [
    { group: "Messages", label: "Send a message", keys: opts.enterToSend ? ["Enter"] : [mod, "Enter"] },
    { group: "Messages", label: "New line", keys: opts.enterToSend ? ["Shift", "Enter"] : ["Enter"] },
    { group: "Messages", label: "Edit your last message", keys: ["↑"] },
    { group: "Messages", label: "Move through suggestions", keys: ["↑", "↓"] },
    { group: "Messages", label: "Pick a suggestion", keys: ["Tab"] },
    { group: "Messages", label: "Cancel reply or edit", keys: ["Esc"] },
    { group: "General", label: "Publish a post", keys: [mod, "Enter"] },
    { group: "General", label: "Close dialogs, menus and panels", keys: ["Esc"] },
    { group: "General", label: "Move through results", keys: ["↑", "↓"] },
    { group: "General", label: "Open the selected result", keys: ["Enter"] },
  ];
}

/** Actions that open overlays work even while typing, as long as they use Ctrl/Alt. */
export const GLOBAL_WHILE_TYPING: KeybindAction[] = ["quickSwitcher", "commandPalette", "shortcutHelp"];
